import { useCallback, useEffect, useState } from 'react'
import type { Draft } from 'immer'
import { produce } from 'immer'
import values from 'lodash/values'
import isString from 'lodash/isString'

export type EventFormErrorMap = Record<string, string | undefined>

export function hasEventFormErrors(errors: EventFormErrorMap): boolean {
  return values(errors).some(Boolean)
}

export function clearEventFormFieldErrors(
  errors: EventFormErrorMap,
  paths: string | readonly string[]
): EventFormErrorMap {
  const next = { ...errors }
  delete next.root
  const fieldPaths = isString(paths) ? [paths] : paths
  for (const path of fieldPaths) {
    delete next[path]
  }
  return next
}

type UseEventFormStateOptions<T> = {
  initial: T
  validate?: (form: T) => EventFormErrorMap
}

export function useEventFormState<T extends Record<string, unknown>>({
  initial,
  validate
}: UseEventFormStateOptions<T>) {
  const [formValues, setFormValues] = useState<T>(initial)
  const [errors, setErrors] = useState<EventFormErrorMap>({})

  useEffect(() => {
    setFormValues(initial)
    setErrors({})
  }, [initial])

  const setField = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setFormValues((current) => ({ ...current, [key]: value }))
    setErrors((current) => clearEventFormFieldErrors(current, key as string))
  }, [])

  const patchValues = useCallback((recipe: (draft: Draft<T>) => void) => {
    setFormValues((current) => produce(current, recipe))
  }, [])

  const setFieldError = useCallback((key: string, message: string) => {
    setErrors((current) => ({ ...current, [key]: message }))
  }, [])

  const validateForm = useCallback((): boolean => {
    const found = validate?.(formValues) ?? {}
    setErrors(found)
    return !hasEventFormErrors(found)
  }, [formValues, validate])

  return {
    values: formValues,
    errors,
    setField,
    patchValues,
    setFieldError,
    setErrors,
    validateForm
  }
}

export type EventFormState<T extends Record<string, unknown> = Record<string, unknown>> = ReturnType<
  typeof useEventFormState<T>
>
